import React, { useEffect, useMemo, useState } from "react";
import { Images, Download, Share2, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { renderMemoryCard } from "@/lib/album";
import { canNativeShare, nativeShare } from "@/lib/share";
import { trackEvent } from "@/lib/analytics";
import { earnPoints } from "@/lib/usePoints";

/** On-device photo album for a trip + memory postcard (canvas, never uploaded). */
export default function TripAlbum({ code, photos, onRemove, country, name, days }) {
  const { lang, t } = useLang();
  const [busy, setBusy] = useState(false);
  const entries = useMemo(() => Object.entries(photos), [photos]);
  const urls = useMemo(() => entries.map(([k, b]) => [k, URL.createObjectURL(b)]), [entries]);
  useEffect(() => () => urls.forEach(([, u]) => URL.revokeObjectURL(u)), [urls]);
  if (!entries.length) return null;
  const make = async () => {
    setBusy(true);
    try {
      const blob = await renderMemoryCard({ blobs: entries.map(([, b]) => b), flagUrl: country?.flag_url, title: country?.short_names?.[lang] || code, subtitle: t("album_card_sub").replace("{name}", name || "").replace("{n}", days).trim(), rtl: lang === "ar" });
      return new File([blob], `atlas-album-${code}.png`, { type: "image/png" });
    } finally { setBusy(false); }
  };
  const download = async () => { const f = await make(); const u = URL.createObjectURL(f); const a = document.createElement("a"); a.href = u; a.download = f.name; a.click(); URL.revokeObjectURL(u); trackEvent("album_card_download", { code }); };
  const share = async () => {
    const f = await make(); const text = t("album_share_text").replace("{country}", country?.short_names?.[lang] || code);
    trackEvent("share", { method: "album", content_type: "album_card", item_id: code }); earnPoints("trip_shared", lang);
    if (canNativeShare() && navigator.canShare?.({ files: [f] })) return nativeShare({ files: [f], title: text, text });
    await download(); toast(t("album_saved"));
  };
  return (
    <section className="rounded-3xl border border-stoneLine bg-card p-4" data-testid="trip-album">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-display font-bold text-lg text-ink inline-flex items-center gap-2"><Images className="w-5 h-5 text-ochre" /> {t("album_title")} <span className="text-sm text-muted-foreground stat-number">{entries.length}</span></h3>
        <div className="flex gap-2">
          <button onClick={download} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full border border-stoneLine px-3 py-1.5 text-sm hover:bg-sandDeep/50 disabled:opacity-50" data-testid="album-download">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {t("album_card")}</button>
          <button onClick={share} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-ochre text-white px-3 py-1.5 text-sm font-semibold hover:bg-ink disabled:opacity-50" data-testid="album-share"><Share2 className="w-3.5 h-3.5" /> {t("trip_share")}</button>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="album-grid">
        {urls.map(([k, u]) => (
          <figure key={k} className="relative aspect-square rounded-2xl overflow-hidden group" data-testid="album-photo">
            <img src={u} alt={k} className="w-full h-full object-cover" />
            <button onClick={() => onRemove(k)} className="absolute top-1.5 end-1.5 w-7 h-7 rounded-full bg-black/55 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity" aria-label={t("delete")} data-testid="album-remove"><Trash2 className="w-3.5 h-3.5" /></button>
          </figure>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">{t("album_private")}</p>
    </section>
  );
}
